import { useState, useEffect, useMemo } from 'react';
import {
    AlertTriangle,
    AlertCircle,
    Search,
    Download,
    Inbox,
    PackageSearch,
    Clock,
    CheckCircle2,
    XCircle,
} from 'lucide-react';
import { useAuthStore } from '../../hooks/useAuth';
import { OperationsFeatureGate, isOperationsTierUser } from '../../components/entitlements/OperationsFeatureGate';
import { api } from '../../lib/api';
import {
    ResponsiveContainer,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Bar,
    BarChart,
} from 'recharts';
import type {
    ReceivingExceptionsResponse,
    ExceptionDetailRow,
} from '../../types/analyticsReceiving';
import { ReceivingExceptionsExport } from '../../components/reports/ReceivingExceptionsExport';

// ── Badge helpers ──────────────────────────────────────────────────

function receiptStatusBadge(status: string | null) {
    if (!status) return null;
    const map: Record<string, { label: string; cls: string }> = {
        complete: { label: '✓ Complete', cls: 'bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800' },
        short: { label: '▼ Short', cls: 'bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800' },
        over: { label: '▲ Over', cls: 'bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800' },
        damaged: { label: '✕ Damaged', cls: 'bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800' },
        refused: { label: '✕ Refused', cls: 'bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800' },
    };
    const cfg = map[status];
    if (!cfg) return <span className="text-xs text-slate-400">{status}</span>;
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${cfg.cls}`}>{cfg.label}</span>;
}

function resolutionBadge(status: string | null) {
    if (!status) return null;
    const map: Record<string, { label: string; cls: string }> = {
        unresolved: { label: 'Unresolved', cls: 'bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800' },
        followup_created: { label: 'Follow-up', cls: 'bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800' },
        supplier_confirmed_missing: { label: 'Confirmed Missing', cls: 'bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800' },
        resolved: { label: '✓ Resolved', cls: 'bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800' },
        written_off: { label: 'Written Off', cls: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700' },
    };
    const cfg = map[status];
    if (!cfg) return <span className="text-xs text-slate-400">{status}</span>;
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${cfg.cls}`}>{cfg.label}</span>;
}

// ── Main Component ─────────────────────────────────────────────────

export function ReceivingExceptions() {
    const [timeRange, setTimeRange] = useState('7d');
    const [data, setData] = useState<ReceivingExceptionsResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [warehouseName, setWarehouseName] = useState('Warehouse');
    const [showExport, setShowExport] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const { token, user } = useAuthStore();
    const isOperationsEntitled = isOperationsTierUser(user);

    // Fetch warehouse name on mount
    useEffect(() => {
        if (!token || !isOperationsEntitled) return;
        api.getWarehouses()
            .then(res => {
                const name = res.warehouses?.[0]?.name;
                if (name) setWarehouseName(name);
            })
            .catch(() => {});
    }, [token]);

    // Fetch data
    useEffect(() => {
        fetchData();
    }, [timeRange, token]);

    const fetchData = async () => {
        if (!token || !isOperationsEntitled) return;
        setIsLoading(true);
        setError(null);
        try {
            const rangeDays = timeRange === '30d' ? 30 : timeRange === '14d' ? 14 : 7;
            const response = await fetch(
                `/api/analytics/receiving-exceptions?rangeDays=${rangeDays}&_ts=${Date.now()}`,
                {
                    cache: 'no-store' as RequestCache,
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Cache-Control': 'no-cache',
                    },
                },
            );
            if (response.status === 304) throw new Error('Stale cache (304) — please retry');
            if (!response.ok) throw new Error(`API error: ${response.status}`);
            const json: ReceivingExceptionsResponse = await response.json();
            setData(json);
        } catch (err: any) {
            console.error('Failed to load receiving exceptions analytics', err);
            setError(err.message || 'Failed to load data');
        } finally {
            setIsLoading(false);
        }
    };

    // ── Filtered detail rows ───────────────────────────────────────
    const filteredDetails = useMemo(() => {
        if (!data) return [];
        let rows = data.details;

        // Status/type filter
        if (statusFilter !== 'all') {
            rows = rows.filter((r) => {
                switch (statusFilter) {
                    case 'short': return r.receiptStatus === 'short';
                    case 'over': return r.receiptStatus === 'over';
                    case 'damaged': return r.receiptStatus === 'damaged' || r.exceptionType === 'damaged';
                    case 'refused': return r.receiptStatus === 'refused' || r.exceptionType === 'refused';
                    case 'pending': return !r.receiptStatus;
                    case 'unresolved': return r.resolutionStatus === 'unresolved';
                    default: return true;
                }
            });
        }

        // Search
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            rows = rows.filter(
                (r) =>
                    r.poNumber?.toLowerCase().includes(q) ||
                    r.carrierName?.toLowerCase().includes(q) ||
                    r.clientName?.toLowerCase().includes(q) ||
                    r.receiptNotes?.toLowerCase().includes(q),
            );
        }
        return rows;
    }, [data, statusFilter, searchQuery]);

    // ── Error state ────────────────────────────────────────────────
    if (!isOperationsEntitled) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-6">
                <OperationsFeatureGate
                    featureName="Receiving Exceptions & Shortage Analysis"
                    description="Aggregated receiving exception analytics, supplier & carrier shortage breakdowns, follow-up PO generation, and resolution workflow tracking are included in the BayWhiz Operations plan ($199 USD/month per facility). Your Starter plan ($99 USD/month) includes full dock receiving, pallet receipt recording, shortage/overage/damage tagging, and standard PO status management."
                    highlights={[
                        'Aggregated shortage, overage, damage, and refusal analytics across suppliers and carriers',
                        'One-click carry-forward follow-up PO generation (-R1) for short receipts',
                        'End-to-end exception resolution tracking and printable receiving audit reports',
                    ]}
                    testId="receiving-exceptions-operations-gate"
                />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
                <div className="flex flex-col items-center gap-4 text-center">
                    <AlertCircle className="w-12 h-12 text-red-500" />
                    <p className="text-slate-500 dark:text-slate-400 text-sm">{error}</p>
                    <button onClick={fetchData} className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-bold hover:bg-orange-700 transition-colors">
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    // ── Loading state ──────────────────────────────────────────────
    if (isLoading || !data) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-slate-200 dark:border-slate-800 border-t-orange-500 rounded-full animate-spin"></div>
                    <p className="text-slate-500 dark:text-slate-400 font-mono text-sm tracking-widest uppercase">
                        Analyzing Receiving Data...
                    </p>
                </div>
            </div>
        );
    }

    const { kpis, receiptStatusBreakdown, exceptionTagBreakdown, dailyTrend, topCarriers, topClients } = data;
    const meta = data._meta;
    const rangeLabel = meta.rangeDays === 30 ? 'Last 30 Days' : meta.rangeDays === 14 ? 'Last 14 Days' : 'Last 7 Days';

    // Compute date range for export (same pattern as DwellAnalytics)
    const exportRangeDays = meta.rangeDays ?? 7;
    const exportEndDate = new Date();
    exportEndDate.setHours(23, 59, 59, 0);
    const exportStartDate = new Date(exportEndDate);
    exportStartDate.setDate(exportStartDate.getDate() - exportRangeDays + 1);
    exportStartDate.setHours(0, 0, 0, 0);

    return (
        <>
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-50 p-6 lg:p-8 font-sans">

            {/* ─── Header ─────────────────────────────────────────── */}
            <header className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50 flex items-center gap-3">
                        Receiving Exceptions Report
                    </h1>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                        Track shortages, damages, overages, refused freight, pending receipts, and unresolved receiving issues.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="hidden md:flex items-center bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-1">
                        {['7d', '14d', '30d'].map((range) => (
                            <button
                                key={range}
                                onClick={() => setTimeRange(range)}
                                className={`px-4 py-1.5 rounded-md text-xs font-bold transition-colors ${
                                    timeRange === range
                                        ? 'bg-orange-600 text-white shadow-sm'
                                        : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                                }`}
                            >
                                {range === '7d' ? '7 Days' : range === '14d' ? '14 Days' : '30 Days'}
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => setShowExport(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-900 dark:bg-slate-50 text-white dark:text-slate-900 rounded-lg text-sm font-bold shadow-sm hover:bg-slate-800 dark:hover:bg-slate-200 transition-colors"
                    >
                        <Download className="w-4 h-4" />
                        <span className="hidden sm:inline">Export Report</span>
                    </button>
                </div>
            </header>

            {/* ─── Metadata bar ────────────────────────────────────── */}
            <div className="flex flex-wrap items-center gap-4 mb-4 text-[11px] text-slate-400 dark:text-slate-500 font-mono">
                <span>Updated: {new Date(Number(meta.computedAt)).toLocaleTimeString()}</span>
                <span>·</span>
                <span>Range: {rangeLabel}</span>
                <span>·</span>
                <span>POs Reviewed: {kpis.totalPOsReviewed}</span>
                <span>·</span>
                <span>Details: {meta.detailCount}</span>
                {meta.isTruncated && (
                    <>
                        <span>·</span>
                        <span className="text-amber-500 dark:text-amber-400 font-semibold">Showing latest {meta.detailLimit} rows</span>
                    </>
                )}
            </div>

            {/* ─── KPI Cards ───────────────────────────────────────── */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
                <KpiCard title="Total Exceptions" value={String(kpis.totalExceptions)} icon={<AlertTriangle className="w-5 h-5 text-orange-500" />} accent={kpis.totalExceptions > 0 ? 'warning' : 'neutral'} />
                <KpiCard title="Exception Rate" value={`${kpis.exceptionRatePct}%`} icon={<AlertCircle className="w-5 h-5 text-red-500" />} accent={kpis.exceptionRatePct > 10 ? 'danger' : 'neutral'} />
                <KpiCard title="Pending Receipts" value={String(kpis.pendingReceiptCount)} icon={<Clock className="w-5 h-5 text-amber-500" />} accent={kpis.pendingReceiptCount > 0 ? 'warning' : 'neutral'} />
                <KpiCard title="Unresolved" value={String(kpis.unresolvedCount)} icon={<XCircle className="w-5 h-5 text-red-500" />} accent={kpis.unresolvedCount > 0 ? 'danger' : 'neutral'} />
                <KpiCard title="Follow-ups" value={String(kpis.followupCreatedCount)} icon={<PackageSearch className="w-5 h-5 text-blue-500" />} accent="neutral" />
                <KpiCard title="Resolved" value={String(kpis.resolvedCount)} icon={<CheckCircle2 className="w-5 h-5 text-emerald-500" />} accent="good" />
            </div>

            {/* ─── Breakdowns (side by side) ────────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">

                {/* Receipt Outcome Breakdown */}
                <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm">
                    <div className="mb-4">
                        <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50">Receipt Outcome Breakdown</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Quantitative receipt status distribution</p>
                    </div>
                    {receiptStatusBreakdown.length > 0 ? (
                        <div className="space-y-3">
                            {receiptStatusBreakdown.map((row) => (
                                <div key={row.status} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-slate-800">
                                    <div className="flex items-center gap-3">
                                        {receiptStatusBadge(row.status)}
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <span className="text-sm font-bold text-slate-900 dark:text-slate-200">{row.count}</span>
                                        <span className="text-xs text-slate-400 w-12 text-right">{row.pct}%</span>
                                        <div className="w-24 h-2 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
                                            <div className="h-full rounded-full bg-orange-500" style={{ width: `${Math.min(row.pct, 100)}%` }} />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <EmptyState message="No receipt status exceptions in this period" />
                    )}
                </div>

                {/* Exception Tag Breakdown */}
                <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm">
                    <div className="mb-4">
                        <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50">Exception Tag Breakdown</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Qualitative exception classifications</p>
                    </div>
                    {exceptionTagBreakdown.length > 0 ? (
                        <div className="space-y-3">
                            {exceptionTagBreakdown.map((row) => (
                                <div key={row.type} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-slate-800">
                                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{row.label}</span>
                                    <div className="flex items-center gap-4">
                                        <span className="text-sm font-bold text-slate-900 dark:text-slate-200">{row.count}</span>
                                        <span className="text-xs text-slate-400 w-12 text-right">{row.pct}%</span>
                                        <div className="w-24 h-2 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
                                            <div className="h-full rounded-full bg-amber-500" style={{ width: `${Math.min(row.pct, 100)}%` }} />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <EmptyState message="No exception tags recorded in this period" />
                    )}
                </div>
            </div>

            {/* ─── Daily Trend Chart ────────────────────────────────── */}
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm mb-8">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50">Daily Exception Trend</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400">PO receipts vs exceptions per day</p>
                    </div>
                    <div className="flex gap-4">
                        <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                            <span className="w-3 h-3 bg-slate-300 dark:bg-slate-600 rounded-sm"></span> Total POs
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                            <span className="w-3 h-3 bg-orange-500 rounded-sm"></span> Exceptions
                        </div>
                    </div>
                </div>
                {dailyTrend.some((t) => t.totalPOs > 0) ? (
                    <div className="h-[280px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={dailyTrend.map((t) => ({ date: t.date.slice(5), totalPOs: t.totalPOs, exceptions: t.exceptionCount }))} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" className="dark:stroke-slate-700/50" vertical={false} />
                                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false} />
                                <Tooltip
                                    contentStyle={{ background: '#1e293b', border: 'none', borderRadius: 8, fontSize: 13, color: '#f1f5f9', boxShadow: '0 4px 12px rgba(0,0,0,.3)' }}
                                    labelStyle={{ color: '#94a3b8', marginBottom: 4 }}
                                />
                                <Bar dataKey="totalPOs" name="Total POs" fill="#cbd5e1" radius={[4, 4, 0, 0]} />
                                <Bar dataKey="exceptions" name="Exceptions" fill="#f97316" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                ) : (
                    <EmptyState message="No receipt data for this period" />
                )}
            </div>

            {/* ─── Top Carriers + Top Clients (side by side) ─────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">

                {/* Top Carriers */}
                <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm">
                    <div className="p-6 border-b border-slate-100 dark:border-slate-800">
                        <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50">Top Carriers</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400">By exception frequency</p>
                    </div>
                    {topCarriers.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-slate-50 dark:bg-slate-950 text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold border-b border-slate-200 dark:border-slate-800">
                                    <tr>
                                        <th className="px-4 py-3">Carrier</th>
                                        <th className="px-4 py-3 text-center">POs</th>
                                        <th className="px-4 py-3 text-center">Exceptions</th>
                                        <th className="px-4 py-3 text-center">Rate</th>
                                        <th className="px-4 py-3 text-center">Short</th>
                                        <th className="px-4 py-3 text-center">Damaged</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                    {topCarriers.map((c) => (
                                        <tr key={c.carrierName} className="hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                                            <td className="px-4 py-3 font-bold text-slate-900 dark:text-slate-200 max-w-[180px] truncate">{c.carrierName}</td>
                                            <td className="px-4 py-3 text-center text-slate-600 dark:text-slate-400">{c.totalPOs}</td>
                                            <td className="px-4 py-3 text-center">
                                                {c.exceptionCount > 0 ? (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-950 text-orange-700 dark:text-orange-400 border border-orange-200 dark:border-orange-800">
                                                        {c.exceptionCount}
                                                    </span>
                                                ) : (
                                                    <span className="text-slate-400">-</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-center font-mono text-xs text-slate-600 dark:text-slate-400">{c.exceptionRatePct}%</td>
                                            <td className="px-4 py-3 text-center text-slate-600 dark:text-slate-400">{c.shortages || '-'}</td>
                                            <td className="px-4 py-3 text-center text-slate-600 dark:text-slate-400">{c.damages || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="p-12"><EmptyState message="No carrier data for this period" /></div>
                    )}
                </div>

                {/* Top Clients */}
                <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm">
                    <div className="p-6 border-b border-slate-100 dark:border-slate-800">
                        <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50">Top Clients / Vendors</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400">By exception frequency</p>
                    </div>
                    {topClients.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-slate-50 dark:bg-slate-950 text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold border-b border-slate-200 dark:border-slate-800">
                                    <tr>
                                        <th className="px-4 py-3">Client / Vendor</th>
                                        <th className="px-4 py-3 text-center">POs</th>
                                        <th className="px-4 py-3 text-center">Exceptions</th>
                                        <th className="px-4 py-3 text-center">Rate</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                    {topClients.map((c) => (
                                        <tr key={c.clientName} className="hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                                            <td className="px-4 py-3 font-bold text-slate-900 dark:text-slate-200 max-w-[200px] truncate">{c.clientName}</td>
                                            <td className="px-4 py-3 text-center text-slate-600 dark:text-slate-400">{c.totalPOs}</td>
                                            <td className="px-4 py-3 text-center">
                                                {c.exceptionCount > 0 ? (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-950 text-orange-700 dark:text-orange-400 border border-orange-200 dark:border-orange-800">
                                                        {c.exceptionCount}
                                                    </span>
                                                ) : (
                                                    <span className="text-slate-400">-</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-center font-mono text-xs text-slate-600 dark:text-slate-400">{c.exceptionRatePct}%</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="p-12"><EmptyState message="No client data for this period" /></div>
                    )}
                </div>
            </div>

            {/* ─── Detail Table ──────────────────────────────────── */}
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm">
                <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50">Exception Details</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                            {filteredDetails.length} row{filteredDetails.length !== 1 ? 's' : ''}
                            {statusFilter !== 'all' && ` (filtered: ${statusFilter})`}
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2 items-center">
                        {/* Filters */}
                        <div className="flex items-center bg-slate-100 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg p-0.5 overflow-x-auto">
                            {[
                                { key: 'all', label: 'All' },
                                { key: 'short', label: 'Short' },
                                { key: 'over', label: 'Over' },
                                { key: 'damaged', label: 'Damaged' },
                                { key: 'refused', label: 'Refused' },
                                { key: 'pending', label: 'Pending' },
                                { key: 'unresolved', label: 'Unresolved' },
                            ].map((f) => (
                                <button
                                    key={f.key}
                                    onClick={() => setStatusFilter(f.key)}
                                    className={`px-3 py-1 rounded-md text-xs font-bold whitespace-nowrap transition-colors ${
                                        statusFilter === f.key
                                            ? 'bg-orange-600 text-white shadow-sm'
                                            : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                                    }`}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>
                        {/* Search */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input
                                type="text"
                                placeholder="PO, carrier, client..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9 pr-4 py-2 h-9 text-sm bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-slate-50 focus:outline-none focus:ring-2 focus:ring-orange-500 w-48"
                            />
                        </div>
                    </div>
                </div>

                {filteredDetails.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left min-w-[1200px]">
                            <thead className="bg-slate-50 dark:bg-slate-950 text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold border-b border-slate-200 dark:border-slate-800">
                                <tr>
                                    <th className="px-4 py-3">Date</th>
                                    <th className="px-4 py-3">Carrier</th>
                                    <th className="px-4 py-3">Dock</th>
                                    <th className="px-4 py-3">PO</th>
                                    <th className="px-4 py-3">Client</th>
                                    <th className="px-4 py-3 text-center">Exp</th>
                                    <th className="px-4 py-3 text-center">Rcv</th>
                                    <th className="px-4 py-3 text-center">Var</th>
                                    <th className="px-4 py-3">Status</th>
                                    <th className="px-4 py-3">Exception</th>
                                    <th className="px-4 py-3">Resolution</th>
                                    <th className="px-4 py-3">Follow-up</th>
                                    <th className="px-4 py-3">Received By</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {filteredDetails.map((row) => (
                                    <DetailRow key={row.appointmentPosId} row={row} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="p-12">
                        <EmptyState message={searchQuery || statusFilter !== 'all' ? 'No rows match your filters' : 'No detail rows for this period'} />
                    </div>
                )}
            </div>
        </div>

            {/* Export modal — mounts only when triggered */}
            {showExport && data && (
                <ReceivingExceptionsExport
                    facilityName={warehouseName}
                    rangeLabel={rangeLabel}
                    startDate={exportStartDate}
                    endDate={exportEndDate}
                    computedAt={Number(meta.computedAt)}
                    kpis={kpis}
                    receiptStatusBreakdown={receiptStatusBreakdown}
                    exceptionTagBreakdown={exceptionTagBreakdown}
                    dailyTrend={dailyTrend}
                    topCarriers={topCarriers}
                    topClients={topClients}
                    details={data.details}
                    isTruncated={meta.isTruncated}
                    onClose={() => setShowExport(false)}
                />
            )}
        </>
    );
}

// ── Sub-components ─────────────────────────────────────────────────

function EmptyState({ message }: { message: string }) {
    return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
            <Inbox className="w-10 h-10 text-slate-300 dark:text-slate-700 mb-3" />
            <p className="text-sm text-slate-400 dark:text-slate-500 font-medium">{message}</p>
        </div>
    );
}

function KpiCard({ title, value, icon, accent }: { title: string; value: string; icon: React.ReactNode; accent: 'danger' | 'warning' | 'good' | 'neutral' }) {
    const trendColor = accent === 'danger'
        ? 'text-red-600 dark:text-red-400'
        : accent === 'warning'
            ? 'text-amber-600 dark:text-amber-400'
            : accent === 'good'
                ? 'text-emerald-600 dark:text-emerald-400'
                : 'text-slate-500 dark:text-slate-400';

    return (
        <div className="bg-white dark:bg-slate-900 p-5 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-700 transition-colors group">
            <div className="flex justify-between items-start mb-2">
                <h3 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{title}</h3>
                <span className="p-2 bg-slate-50 dark:bg-slate-800 rounded-lg text-slate-400 group-hover:text-slate-900 dark:group-hover:text-slate-200 transition-colors">
                    {icon}
                </span>
            </div>
            <div className={`text-2xl font-bold tracking-tight ${trendColor}`}>{value}</div>
        </div>
    );
}

function DetailRow({ row }: { row: ExceptionDetailRow }) {
    const formattedDate = row.appointmentDate
        ? new Date(row.appointmentDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        : '-';

    const varianceCls = row.variance !== null
        ? row.variance < 0
            ? 'text-red-600 dark:text-red-400 font-bold'
            : row.variance > 0
                ? 'text-blue-600 dark:text-blue-400 font-bold'
                : 'text-slate-400'
        : 'text-slate-400';

    return (
        <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors text-xs">
            <td className="px-4 py-3 text-slate-600 dark:text-slate-400 whitespace-nowrap">{formattedDate}</td>
            <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-200 max-w-[140px] truncate">{row.carrierName}</td>
            <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{row.dockNumber || '-'}</td>
            <td className="px-4 py-3 font-mono font-bold text-slate-900 dark:text-slate-200 max-w-[120px] truncate">{row.poNumber}</td>
            <td className="px-4 py-3 text-slate-600 dark:text-slate-400 max-w-[130px] truncate">{row.clientName}</td>
            <td className="px-4 py-3 text-center text-slate-600 dark:text-slate-400">{row.expectedQty ?? '-'}</td>
            <td className="px-4 py-3 text-center text-slate-600 dark:text-slate-400">{row.receivedQty ?? '-'}</td>
            <td className={`px-4 py-3 text-center ${varianceCls}`}>
                {row.variance !== null ? (row.variance > 0 ? `+${row.variance}` : String(row.variance)) : '-'}
            </td>
            <td className="px-4 py-3">{receiptStatusBadge(row.receiptStatus)}</td>
            <td className="px-4 py-3">
                {row.exceptionLabel ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800">
                        ⚠ {row.exceptionLabel}
                    </span>
                ) : (
                    <span className="text-slate-400">-</span>
                )}
            </td>
            <td className="px-4 py-3">{resolutionBadge(row.resolutionStatus)}</td>
            <td className="px-4 py-3 font-mono text-xs text-blue-600 dark:text-blue-400">
                {row.followupPoNumber || '-'}
            </td>
            <td className="px-4 py-3 text-slate-600 dark:text-slate-400 max-w-[100px] truncate">{row.receivedBy || '-'}</td>
        </tr>
    );
}
